import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, TrendingUp, Target, BookOpen, ShieldCheck } from 'lucide-react';
import { type ProficiencyData } from '@/utils/technicianPassportUtils';
import type { DirectMetrics } from '@/hooks/useDirectTechnicianMetrics';
import { PassportSectionFallback } from './PassportSectionFallback';
import { PassportProvenanceBadge } from './PassportProvenanceBadge';

interface PassportFutureInsightsProps {
  proficiencies: ProficiencyData[];
  metrics: DirectMetrics | null;
  /**
   * Either input FAILED to load. Every insight here is computed from those two
   * inputs, so computing them from an empty set would invent a reading of the
   * person's trajectory out of a failed request — the sharpest form of CORE-C's
   * "failure is not a fact".
   */
  isError?: boolean;
}

/**
 * Core-100 R4 (CORE-H). Every insight is three separately labelled parts:
 *   evidence        — what was measured, with its sample (n) and its basis;
 *   interpretation  — what it MAY mean (never stated as fact);
 *   recommendation  — an optional development suggestion WITH its reason.
 *
 * And the SHALLs the previous version broke:
 *   - no trend without a time dimension: every input here is a single snapshot (an average or a current level), so
 *     no insight says "consistently", "improving" or "on track" — a point value is not a trajectory;
 *   - adequate sample before a pattern: an efficiency or quality reading needs MIN_JOBS_FOR_PATTERN completed jobs,
 *     a category reading its own sample threshold; below that there is no insight (a 1-job technician gets none);
 *   - no employment / promotion / succession / mobility implication: no "specialist", "mentor candidate" or role
 *     language — insights are about development, never about who should hold which role.
 */
export const MIN_JOBS_FOR_PATTERN = 20;
export const MIN_CATEGORY_SAMPLE = 10;

export type InsightKind = 'strength' | 'development' | 'quality' | 'efficiency' | 'none';
export interface FutureInsight {
  kind: InsightKind;
  evidence: string;
  interpretation: string;
  recommendation: { text: string; reason: string } | null;
}

const pct = (ratio: number) => Math.round(ratio * 100);

export function buildFutureInsights(proficiencies: ProficiencyData[], metrics: DirectMetrics | null): FutureInsight[] {
  const out: FutureInsight[] = [];
  // CORE-C GAP-007: unknown never satisfies a threshold and is never read as zero.
  const sampled = (p: ProficiencyData, min: number) => p.sample_size !== null && p.sample_size >= min;

  const strong = proficiencies
    .filter((p) => sampled(p, MIN_CATEGORY_SAMPLE) && p.proficiency_score >= 80
      && p.confidence_score !== null && p.confidence_score >= 0.7)
    .slice(0, 2);
  if (strong.length > 0) {
    out.push({
      kind: 'strength',
      evidence: strong.map((p) => `${p.category_key}: proficiency ${Math.round(p.proficiency_score)} across ${p.sample_size} completed jobs (confidence ${pct(p.confidence_score as number)}%)`).join('; ') + '. Current level, not a trend.',
      interpretation: `${strong.map((p) => p.category_key).join(' and ')} may be a relative strength at this point.`,
      recommendation: null,
    });
  }

  for (const p of proficiencies
    .filter((q) => sampled(q, MIN_CATEGORY_SAMPLE) && q.proficiency_score >= 60 && q.proficiency_score < 80)
    .slice(0, 2)) {
    out.push({
      kind: 'development',
      evidence: `${p.category_key}: proficiency ${Math.round(p.proficiency_score)} across ${p.sample_size} completed jobs. Current level, not a trend.`,
      interpretation: `${p.category_key} may be an area that is still developing.`,
      recommendation: { text: `More ${p.category_key} work would add evidence.`, reason: 'a mid-range level with a meaningful sample' },
    });
  }

  for (const p of proficiencies.filter((q) => sampled(q, 5) && q.proficiency_score < 50).slice(0, 2)) {
    out.push({
      kind: 'development',
      evidence: `${p.category_key}: proficiency ${Math.round(p.proficiency_score)} across ${p.sample_size} completed jobs.`,
      interpretation: `${p.category_key} may be an area with less experience so far.`,
      recommendation: { text: `Recommend more exposure to ${p.category_key} work.`, reason: `proficiency below 50 over ${p.sample_size} jobs` },
    });
  }

  const jobs = metrics?.jobs_completed ?? 0;
  if (metrics && jobs >= MIN_JOBS_FOR_PATTERN && typeof metrics.avg_efficiency_book === 'number' && metrics.avg_efficiency_book >= 1.05) {
    out.push({
      kind: 'efficiency',
      evidence: `Average book-time efficiency ${pct(metrics.avg_efficiency_book)}% over ${jobs} completed jobs (one average, not a series over time).`,
      interpretation: 'Work may often be finished within book time. Faster is not proof of better work.',
      recommendation: null,
    });
  }
  if (metrics && jobs >= MIN_JOBS_FOR_PATTERN && typeof metrics.comeback_rate === 'number' && metrics.comeback_rate < 0.02) {
    out.push({
      kind: 'quality',
      evidence: `Comeback rate ${(metrics.comeback_rate * 100).toFixed(1)}% over ${jobs} completed jobs.`,
      interpretation: 'This may reflect a low rate of repeat repairs over that work.',
      recommendation: null,
    });
  }

  if (out.length === 0) {
    out.push({
      kind: 'none',
      evidence: 'Not enough completed work yet to read a pattern.',
      interpretation: 'Insights appear once there is enough evidence behind them.',
      recommendation: null,
    });
  }
  return out.slice(0, 4);
}

const ICON: Record<InsightKind, typeof Sparkles> = {
  strength: Target, development: BookOpen, quality: ShieldCheck, efficiency: TrendingUp, none: Sparkles,
};

export function PassportFutureInsights({
  proficiencies,
  metrics,
  isError = false,
}: PassportFutureInsightsProps) {
  if (isError) {
    return <PassportSectionFallback sectionName="Future Insights" />;
  }

  const insights = buildFutureInsights(proficiencies, metrics);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Future Development Insights
        </CardTitle>
        <p className="text-xs text-muted-foreground" data-testid="insights-boundary">
          Interpretations of your evidence for development only. They are not decisions about employment, promotion or role.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {insights.map((insight, index) => {
            const Icon = ICON[insight.kind];
            return (
              <div key={index} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/40" data-testid="future-insight" data-kind={insight.kind}>
                <Icon className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="space-y-1 text-sm">
                  <p data-testid="insight-evidence">
                    <span className="font-medium">Evidence: </span>{insight.evidence}
                    {/* Core-100 R5 (CORE-C F/Prov): the evidence statement is a CORE-H derivation and carries the
                        approved badge. The interpretation (below) is an inference and carries "Inferred by OptiBay".
                        The 'none' line states an absence, not a fact. */}
                    {insight.kind !== 'none' && (
                      <PassportProvenanceBadge factGroupId="future-insight-evidence" className="ml-2 align-middle" />
                    )}
                  </p>
                  <p data-testid="insight-interpretation"><span className="font-medium">What it may mean: </span>
                    {/* Founder 2026-10-15: the interpretation is an inference — "Inferred by OptiBay"; the evidence line
                        above states the sample it rests on. The 'none' line is an absence and carries no badge. */}
                    {insight.kind !== 'none' && (
                      <PassportProvenanceBadge factGroupId="future-insight-interpretation" className="mr-1 align-middle" />
                    )}
                    {insight.interpretation}</p>
                  {insight.recommendation && (
                    <p data-testid="insight-recommendation">
                      <span className="font-medium">Suggestion: </span>{insight.recommendation.text}
                      <span className="text-muted-foreground"> (because: {insight.recommendation.reason})</span>
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
